/**
 * THE WORDS THIS SHOP'S SCREENS USE, WITHOUT ASKING AGAIN.
 *
 * src/domain/trade.ts holds the vocabulary and stays pure -- no imports, no
 * state, so the server can use it too. This is the UI's copy of the answer:
 * primed once at boot from the account whoAmI() already loaded, and read
 * synchronously everywhere after.
 *
 * WHY SYNCHRONOUS MATTERS. Nearly every render function in this app builds
 * a template literal and returns a string. Making them await a lookup would
 * mean making them async, and that spreads: paintCustomer, the wizard, the
 * row builders, everything that calls them. One await at boot is the whole
 * cost, and no drawing function has to change shape to say "door".
 *
 * BEFORE PRIMING IT IS 'auto'. That is deliberate rather than an empty
 * state to guard: a screen drawn before boot finished would otherwise have
 * no words at all, and auto is what every shop was before migration 0107.
 */

import { anA, cap, words, type Vocabulary } from "../domain/trade.ts";

let current: Vocabulary = words("auto");
let trade = "auto";
let name = "the shop";

/** Called once at boot, with the signed-in shop's trade and name. */
export function primeWords(t: string | null | undefined, shopName?: string | null): void {
  trade = t ?? "auto";
  current = words(trade);
  const n = String(shopName ?? "").replace(/,?\s*(llc|inc|ltd|co)\.?$/i, "").trim();
  if (n) name = n;
}

/**
 * THE SHOP'S NAME, FOR THINGS A CUSTOMER READS.
 *
 * Anthony, 2026-09-16: "Remove any mention of supercanic."
 *
 * Two messages sent to customers said Supercanic outright -- the invoice
 * text and the subject of the customer-page email -- so a Swift customer
 * got a bill from a company they have never heard of. Whoever is signed in
 * is the shop sending it, so this is read from their own account rather
 * than from a constant.
 */
export function shopName(): string {
  return name;
}

/** The words for the shop that is signed in. Safe to call while drawing. */
export function shopWords(): Vocabulary {
  return current;
}

/**
 * The trade itself, for the rules that take one rather than words --
 * isIdentified(), stepsFor(). Kept beside the words so there is one primed
 * answer and not two that can disagree.
 */
export function currentTrade(): string {
  return trade;
}

/**
 * ─── WHY THIS MOVED OUT OF THE WALKTHROUGHS PAGE ───────────────────────
 *
 * say() was written for src/pages/walkthroughs.ts and lived there, so the
 * process page next door could not reach it. That page did its own
 * substitution by hand with `${w.thing}` and covered four steps, which is
 * how an investigator ended up reading "Get the parts" and "Parts price at
 * what the customer pays" in his own eighteen step manual.
 *
 * It belongs beside shopWords() because that is what it renders. One
 * vocabulary, one renderer, and a page that wants the shop's own words
 * imports it rather than reinventing half of it.
 */
/**
 * THE MANUAL DOES NOT SAY CAR TO A LOCKSMITH.
 *
 * Three trades have words here now and there will be more. The wizard
 * already substitutes them -- `stepsFor(track, trade)` swaps the identify
 * step's title and sub out of src/domain/trade.ts -- so a manual with
 * "a plate or a VIN" typed into it was telling a lock shop to read a
 * number off something that has never had one, while the screen beside it
 * said the right thing.
 *
 * TRACKS is a module constant and the words are not known until a session
 * has loaded, so this runs at render rather than at import. `{identify}`
 * resolves to the trade's OWN sentence, which means the manual and the
 * wizard are now literally the same string and cannot drift.
 *
 * Replacement is a function rather than a string on purpose: String.replace
 * eats `$` patterns in a plain replacement, which fails silently rather
 * than loudly.
 */
export const say = (s: string): string => {
  const w = shopWords();
  return s
    .replace(/\{thing\}/g, () => w.thing)
    .replace(/\{things\}/g, () => w.things)
    .replace(/\{identifyTitle\}/g, () => w.identifyTitle)
    .replace(/\{identify\}/g, () => w.identifySub)
    /* Both of these RESOLVE TO NOTHING on a trade that does not have the
       thing, rather than to a sentence hedged with "if you have one". A
       lock shop being told about VIN decoding is the manual describing a
       screen they will never see. Each carries its own leading space or
       comma so the sentence closes up when it is empty. */
    .replace(/\{vin\}/g, () =>
      w.hasPlate ? " Paste the VIN and press decode rather than typing three fields." : "")
    .replace(/\{reading\}/g, () => (w.reading ? `, and the ${w.reading}` : ""))
    /* Anthony, 2026-09-18: "do the vocabulary pass for technician and
       parts." The Role value is still `technician` and the line kind is
       still `part` -- these are only what the manual CALLS them. */
    .replace(/\{tech\}/g, () => w.techLabel)
    /* THE SHORT FORM, WITH ITS OWN ARTICLE, and neither half is derivable.
       The process page has always said "so a tech can put the phone down".
       Rendering {tech} there silently reworded four trades' manuals to
       "technician", which reading the diff did not show and diffing auto's
       whole rendered page against the deployed one did. "tech" is short
       for technician and for nothing else, so a trade whose people are
       called something else gets the whole word -- and then it needs the
       article, because a token without one writes "a investigator". */
    .replace(/\{atech\}/g, () => {
      const t = w.techLabel === "technician" ? "tech" : w.techLabel;
      return `${/^[aeiou]/i.test(t) ? "an" : "a"} ${t}`;
    })
    .replace(/\{techs\}/g, () => w.techLabelPlural)
    .replace(/\{Techs\}/g, () => cap(w.techLabelPlural))
    .replace(/\{Tech\}/g, () => cap(w.techLabel))
    .replace(/\{part\}/g, () => w.partLabel)
    .replace(/\{parts\}/g, () => w.partsLabel)
    /* The process page opens a sentence with it: "Parts price at what the
       customer pays." Capitalised here rather than at the call site, so
       the page never has to know that "expenses" is one word and not two. */
    .replace(/\{Parts\}/g, () => cap(w.partsLabel))
    /* The whole clause goes, not just the noun. "The old part beside the
       new one" describes a swap, and three of the five trades here do not
       swap anything out -- an investigator least of all. It carries its
       own leading space so the sentence closes up. */
    .replace(/\{oldpart\}/g, () =>
      w.hasEngine ? ` The finished work, and the old ${w.partLabel} beside the new one.`
        : ` The finished work.`)
    /* ─── THE GARAGE IDIOM, NOT JUST THE NOUN ─────────────────────────
       Anthony, 2026-09-18: "I need the run books to match the companies /
       trades."
       {thing} fixed the nouns somebody remembered to wrap. Twenty
       sentences were never wrapped at all, and they are the ones a
       locksmith notices: "work at the car", "walk the car before anything
       comes apart", "the last radiator in the county". These resolve to
       AUTO'S EXACT ORIGINAL WORDING when hasEngine, so the two shops
       already reading this page read every byte they read yesterday, and
       to trade-neutral English otherwise. hasEngine is the key the
       runbook and the spine both use: true for auto alone. */
    .replace(/\{onsite\}/g, () => (w.hasEngine ? "at the car" : "in the field"))
    .replace(/\{thecar\}/g, () => (w.hasEngine ? "the car" : `the ${w.thing}`))
    /* anA, not a bare "a": an RV is "an RV". Every noun before it started
       with a consonant, so none of them moves. */
    .replace(/\{acar\}/g, () => (w.hasEngine ? "a car" : anA(w.thing)))
    /* hasEngine first, not `reading` first. Auto's reading is "odometer"
       but this sentence has always said "the miles", and deriving it
       silently reworded the technician's own step. */
    .replace(/\{miles\}/g, () =>
      (w.hasEngine ? ", the miles," : w.reading ? `, the ${w.reading},` : ","))
    .replace(/\{knownwork\}/g, () =>
      (w.hasEngine ? "brakes or a water pump" : "work you can already name"))
    .replace(/\{unknownwork\}/g, () =>
      (w.hasEngine ? "An unknown noise" : "Work you cannot name yet"))
    .replace(/\{lastpart\}/g, () =>
      (w.hasEngine ? "the last radiator in the county" : "the last one in the county"))
    .replace(/\{thatcar\}/g, () => (w.hasEngine ? "that car" : `that ${w.thing}`))
    .replace(/\{Thing\}/g, () => cap(w.thing))
    .replace(/\{aserial\}/g, () => `a ${w.serial}`)
    .replace(/\{theplate\}/g, () => (w.hasPlate ? "the plate" : `the ${w.serial}`))
    .replace(/\{ymm\}/g, () => (w.hasEngine ? "year, make and model"
      : `${w.yearLabel.toLowerCase()}, ${w.makeLabel.toLowerCase()} and ${w.modelLabel.toLowerCase()}`))
    .replace(/\{fleetunit\}/g, () => (w.hasEngine ? "van" : w.thing))
    /* Two spellings on purpose. The comms track hyphenates it and the
       portal track does not, and both were already written that way -- so
       auto keeps whichever it had rather than being quietly standardised. */
    .replace(/\{whereismy\}/g, () => (w.hasEngine ? "where-is-my-car" : "where-is-my-job"))
    .replace(/\{whereismycall\}/g, () => (w.hasEngine ? "where is my car" : "where is my job"));
};
